import { useState, useCallback } from "react";
import { Modal, Group, Button, Text, Stack, Divider } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons-react";
import {
  useUpdateSpecimenGrossing,
  useCaseForEmbedding,
} from "../../hooks/useWorkflows";
import SpecimenGrossingFields from "./SpecimenGrossingFields";
import type { SpecimenGrossingFieldValues } from "./SpecimenGrossingFields";
import type { FragmentCountPatch } from "../../utils/fragmentCount";

interface GrossDescriptionEditorModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  specimen: any;
  onSaveSuccess?: () => void;
}

export default function GrossDescriptionEditorModal({
  opened,
  onClose,
  caseId,
  specimen,
  onSaveSuccess,
}: GrossDescriptionEditorModalProps) {
  const [fieldValues, setFieldValues] = useState<SpecimenGrossingFieldValues>(
    {}
  );
  const [hasChanges, setHasChanges] = useState(false);

  const updateSpecimenMutation = useUpdateSpecimenGrossing();
  const { data: caseData } = useCaseForEmbedding(caseId);

  const handleFieldChange = useCallback(
    (field: string, value: string | number | null) => {
      setFieldValues((prev) => ({
        ...prev,
        [field]: value,
      }));
      setHasChanges(true);
    },
    []
  );

  const handleFragmentCountChange = useCallback((patch: FragmentCountPatch) => {
    setFieldValues((previous) => ({ ...previous, ...patch }));
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    if (!specimen?.id || Object.keys(fieldValues).length === 0) return;

    // Check if case status has progressed past the editable window (FR-011)
    const currentStatus = caseData?.currentStatus?.name;
    const editableStatuses = [
      "Pending Tissue Processing",
      "Embedding",
      "Pending Grossing",
      "Grossing",
      "Pending Cutting",
      "Cutting",
    ];
    if (currentStatus && !editableStatuses.includes(currentStatus)) {
      notifications.show({
        title: "Warning",
        message:
          "Case has progressed past cutting — changes saved but may require review",
        color: "yellow",
        autoClose: 5000,
      });
    }

    try {
      // If grossDescriptionSource was ai_extracted and user edited, change to manual
      const data: any = { ...fieldValues };
      if (
        specimen.grossDescriptionSource === "ai_extracted" &&
        fieldValues.gross !== undefined
      ) {
        data.grossDescriptionSource = "manual";
      }

      await updateSpecimenMutation.mutateAsync({
        caseId,
        specimenId: specimen.id,
        data,
      });

      notifications.show({
        title: "Saved",
        message: "Gross description updated successfully",
        color: "green",
      });

      setFieldValues({});
      setHasChanges(false);
      onSaveSuccess?.();
      onClose();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message:
          error?.response?.data?.error ||
          error?.message ||
          "Failed to save changes",
        color: "red",
      });
    }
  };

  const handleClose = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    // Reset and close without saving
    setFieldValues({});
    setHasChanges(false);
    onClose();
  };

  // Attribution info
  const grossedBy = specimen?.grossedBy?.fullName;
  const grossedAt = specimen?.grossedAt
    ? new Date(specimen.grossedAt).toLocaleString()
    : null;

  return (
    <Modal.Root
      opened={opened}
      onClose={handleClose}
      size="xl"
      centered
      closeOnClickOutside={!hasChanges}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Stack gap={2}>
              <Text fw={600} size="lg">
                Edit Grossing Description
              </Text>
              <Text size="sm" c="dimmed">
                Specimen {specimen?.number}
                {specimen?.source?.name ? ` — ${specimen.source.name}` : ""}
              </Text>
            </Stack>
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>

        <Modal.Body style={{ flex: 1, overflow: "auto" }}>
          <Stack gap="md">
            <SpecimenGrossingFields
              specimen={specimen}
              values={fieldValues}
              onChange={handleFieldChange}
              onFragmentCountChange={handleFragmentCountChange}
              disabled={updateSpecimenMutation.isPending}
            />

            {grossedBy && (
              <Text size="xs" c="dimmed">
                Last grossed by {grossedBy}
                {grossedAt ? ` on ${grossedAt}` : ""}
              </Text>
            )}
          </Stack>
        </Modal.Body>

        <Divider />
        <Group justify="space-between" p="md">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={updateSpecimenMutation.isPending}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
